import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';

import { selectors, useAppSelector } from 'state/global-state';
import { getConfigValue } from 'utils/environment';
import { isProduction } from 'utils/environment';
import { ISOs } from 'utils/form/constants';
import { logger } from 'utils/logger';
import { routes } from 'utils/routing';

import { usePaymentContext } from '../../state';
import {
  CardTypes,
  IPaymentDetails,
  IPaymentPayload,
  PaymentRequestError,
  RequestPaymentFn,
  paymentErrorType,
} from '../../types';
import { getNonce } from '../first-data/client';

import { ICashAppResult } from './types';
import { getQueryParams, loadScript, removeAllQueryParams } from './web-utils';

declare global {
  interface Window {
    CashApp?: unknown;
  }
}

type AfterPayButton = HTMLElement & {
  renderCashAppPayButton: (options: any) => void;
  initializeForCashAppPay: (options: any) => void;
  initializeCashAppPayListeners: (options: any) => void;
  restartCashAppPay: () => void;
};

const elementId = 'afterpay-button';

const SCRIPTS = [
  {
    id: elementId,
    url: isProduction
      ? 'https://static.afterpay.com/afterpay-button.js'
      : 'https://static-us.afterpay.com/javascript/button/afterpay-button.js',
  },
];

async function embedScripts(): Promise<void> {
  for (const script of SCRIPTS) {
    await loadScript(script.id, script.url);
  }
}

const createAfterPayButton = (cashAppMerchantId: string) => {
  const existingButton = document.querySelector(elementId);
  if (existingButton) {
    return existingButton;
  }

  // Create the afterpay-button element
  const afterPayButton = document.createElement(elementId);
  afterPayButton.setAttribute('merchantPublicKey', cashAppMerchantId);
  afterPayButton.setAttribute('isCashAppPay', 'true');

  // Append the button directly to the body
  document.body.appendChild(afterPayButton);
  return afterPayButton;
};

const getAfterPayButton = () => {
  return document.querySelector(elementId) as AfterPayButton;
};

const disposeAfterPayButton = () => {
  const afterPayButton = document.querySelector(elementId);
  if (afterPayButton) {
    document.body.removeChild(afterPayButton);
  }
  removeAllQueryParams();
};

const bindAttributesToButton = (button: AfterPayButton, amount: number, currency = 'USD') => {
  button.setAttribute('amount', (amount / 100).toFixed(2));
  button.setAttribute('currency', currency);
};

const getRedirectConfirmUrl = (tip: number, pricedOrderId?: string) => {
  if (window.location.pathname.includes(routes.cartCheckout) && pricedOrderId) {
    return `${window.location.origin}${window.location.pathname}?tip=${tip}&rbiOrderId=${pricedOrderId}&isRedirected=true`;
  }
  return `${window.location.origin}${window.location.pathname}?tip=${tip}&isRedirected=true`;
};

type PromiseCallbacks = {
  resolve: (value: IPaymentDetails) => void;
  reject: (error: PaymentRequestError) => void;
};

export const useRequestCashAppPayment: RequestPaymentFn = ({ cashapp: cashAppPaymentInput }) => {
  const { total = 0, tip = 0, onRedirectComplete, onRedirectStateChange } =
    cashAppPaymentInput ?? {};
  const { loading, getEncryptionDetails } = usePaymentContext();
  const pricedOrderId = useAppSelector(selectors.ordering.selectPricedOrderId);
  const redirectConfirmUrl = getRedirectConfirmUrl(tip, pricedOrderId ?? undefined);
  const [isRedirected, setIsRedirected] = useState(
    () => (getQueryParams() as { isRedirected?: string }).isRedirected === 'true'
  );
  const initErrorCount = useRef(0);
  const redirectInitializedRef = useRef(false);
  const activePromiseRef = useRef<PromiseCallbacks | null>(null);

  useLayoutEffect(() => {
    if (isRedirected) {
      onRedirectStateChange?.(true);
    }
  }, [isRedirected, onRedirectStateChange]);

  const resolvePromise = (details: IPaymentDetails) => {
    activePromiseRef.current?.resolve?.(details);
    activePromiseRef.current = null;
  };

  const rejectPromise = (errorType: paymentErrorType) => {
    activePromiseRef.current?.reject?.(new PaymentRequestError(errorType));
    activePromiseRef.current = null;
  };

  const resetRedirectRecovery = useCallback(() => {
    disposeAfterPayButton();
    redirectInitializedRef.current = false;
    setIsRedirected(false);
    onRedirectStateChange?.(false);
  }, [onRedirectStateChange]);

  const { cashAppMerchantId, baseUrl } = getConfigValue({
    key: 'firstData',
    defaultValue: {},
  });

  const init = useCallback(async () => {
    try {
      await embedScripts();
      createAfterPayButton(cashAppMerchantId);
      const afterPayButton = getAfterPayButton();

      if (!afterPayButton) {
        if (isRedirected) {
          resetRedirectRecovery();
        }
        return;
      }

      // wait for afterpay sdk to be loaded
      await new Promise(resolve => setTimeout(resolve, 300));

      const resetCashAppPay = (errorType: paymentErrorType) => {
        rejectPromise(errorType);
        afterPayButton.restartCashAppPay();
        if (isRedirected) {
          resetRedirectRecovery();
        } else {
          disposeAfterPayButton();
          onRedirectStateChange?.(false);
        }
      };

      const eventListeners = {
        CUSTOMER_REQUEST_DECLINED: () => {
          logger.warn({
            isPaymentLog: true,
            message:
              '[useRequestCashAppPayment][init]: Error occurred while requesting Cash App payment.',
            event: 'CUSTOMER_REQUEST_DECLINED',
          });
          resetCashAppPay(paymentErrorType.cashAppDeclined);
        },
        CUSTOMER_REQUEST_FAILED: () => {
          logger.error({
            isPaymentLog: true,
            message:
              '[useRequestCashAppPayment][init]: Error occurred while requesting Cash App payment.',
            event: 'CUSTOMER_REQUEST_FAILED',
          });
          resetCashAppPay(paymentErrorType.cashAppGeneric);
        },
        CUSTOMER_DISMISSED: () => {
          logger.warn({
            isPaymentLog: true,
            message:
              '[useRequestCashAppPayment][init]: Error occurred while requesting Cash App payment.',
            event: 'CUSTOMER_DISMISSED',
          });
          resetCashAppPay(paymentErrorType.cashAppDeclined);
        },
      };

      const onError = (event: unknown) => {
        logger.warn({
          isPaymentLog: true,
          message:
            '[useRequestCashAppPayment][init]: Error occurred while initializing Cash App payment.',
          event,
          isRedirected,
        });

        if (afterPayButton.restartCashAppPay) {
          afterPayButton.restartCashAppPay();
          disposeAfterPayButton();
          initErrorCount.current++;
          if (initErrorCount.current < 3) {
            init();
          } else {
            rejectPromise(paymentErrorType.cashAppGeneric);
            if (isRedirected) {
              resetRedirectRecovery();
            }
          }
        } else {
          rejectPromise(paymentErrorType.cashAppGeneric);
          if (isRedirected) {
            resetRedirectRecovery();
          }
          logger.error({
            isPaymentLog: true,
            message:
              '[useRequestCashAppPayment][init]: Error occurred while initializing Cash App payment after 3 attempts.',
            event,
          });
        }
      };

      const onComplete = async (cardInfo: { data: ICashAppResult }) => {
        try {
          const encryptionDetails = await getEncryptionDetails();
          const { data: cardData } = cardInfo;

          const payload: IPaymentPayload = {
            billingAddress: {
              unitNumber: null,
              locality: null,
              postalCode: '',
              region: null,
              streetAddress: null,
              country: ISOs.US,
            },
            cardNumber: cardData.virtualCard.cardNumber,
            cardType: CardTypes.VISA,
            expiryDate: {
              month: cardData.virtualCard.expiryMonth,
              year: cardData.virtualCard.expiryYear,
            },
            nameOnCard: '',
            securityCode: cardData.virtualCard.cvc,
          };

          const nonceResponse = await getNonce(
            payload,
            encryptionDetails.fdPublicKey,
            encryptionDetails.fdApiKey,
            encryptionDetails.fdAccessToken,
            encryptionDetails.fdCustomerId || null,
            baseUrl,
            true,
            encryptionDetails.algorithm || undefined
          );
          const nonce = await nonceResponse.json();
          const details: IPaymentDetails = {
            cashappDetails: {
              ...cardData,
              fdAccessToken: encryptionDetails.fdAccessToken,
              fdNonce: nonce.token.tokenId,
            },
          };

          if (isRedirected) {
            if (!onRedirectComplete) {
              throw new Error('Cash App redirect completion handler is not available');
            }
            await onRedirectComplete(details);
            resetRedirectRecovery();
            return;
          }

          resolvePromise(details);
        } catch (err) {
          logger.error({
            isPaymentLog: true,
            message: '[useRequestCashAppPayment][onComplete]: Error processing Cash App payment.',
            error: err,
          });
          rejectPromise(paymentErrorType.cashAppGeneric);
          if (isRedirected) {
            resetRedirectRecovery();
          }
        }
      };

      const cashAppPayListenerOptions = {
        redirectConfirmUrl,
        eventListeners,
        onComplete,
        onError,
      };

      const cashAppPayButtonOptions = {
        button: false,
        manage: false,
        eventListeners,
        onBegin: ({ begin }: { begin: () => void }) => {
          bindAttributesToButton(afterPayButton, total);
          afterPayButton.initializeForCashAppPay(cashAppPayListenerOptions);
          begin();
        },
      };

      if (isRedirected) {
        afterPayButton.initializeCashAppPayListeners({
          countryCode: 'US',
          cashAppPayListenerOptions,
        });
        return;
      }

      afterPayButton.renderCashAppPayButton({
        countryCode: 'US',
        cashAppPayButtonOptions,
      });
    } catch (error) {
      logger.error({
        isPaymentLog: true,
        message:
          '[useRequestCashAppPayment][init]: Error occurred while initializing Cash App payment.',
        error,
      });
      rejectPromise(paymentErrorType.cashAppGeneric);
      if (isRedirected) {
        resetRedirectRecovery();
      } else {
        disposeAfterPayButton();
      }
    }
  }, [
    baseUrl,
    cashAppMerchantId,
    getEncryptionDetails,
    isRedirected,
    onRedirectComplete,
    onRedirectStateChange,
    redirectConfirmUrl,
    resetRedirectRecovery,
    total,
  ]);

  useEffect(() => {
    if (isRedirected && !loading && total > 0 && !redirectInitializedRef.current) {
      redirectInitializedRef.current = true;
      init();
    }
  }, [init, isRedirected, loading, total]);

  const requestPayment = useCallback((): Promise<IPaymentDetails | undefined> => {
    // Cancel any existing promise before creating a new one
    rejectPromise(paymentErrorType.cashAppGeneric);

    const afterPayButton = getAfterPayButton();
    initErrorCount.current = 0;
    if (afterPayButton) {
      afterPayButton.restartCashAppPay();
      disposeAfterPayButton();
    }

    return new Promise<IPaymentDetails | undefined>((resolve, reject) => {
      activePromiseRef.current = { resolve, reject: reject as PromiseCallbacks['reject'] };
      init();
    });
  }, [init]);

  return { requestPayment };
};
